import React, { useState, useEffect, useRef } from "react";
import moment from "moment";
import TransferInRequestService from "../../services/transfer-in-request.service";
import { Pagination } from "../Pagination";
import Loading from "../Loading";
import Swal from "sweetalert2";
import "./index.scss";

const TransferReportsTable = ({ pharmacyBranchId, pharmacyBranchName, fromDate, toDate }) => {
    const [transfers, setTransfers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(50);

    // Filters
    const [statusFilter, setStatusFilter] = useState(""); // Empty for all, 3 for accepted
    const [paymentFilter, setPaymentFilter] = useState(""); // Empty for all, 1 for paid, 0 for unpaid

    // Bulk selection state
    const [selectedTransfers, setSelectedTransfers] = useState(new Set());
    const [isAllSelected, setIsAllSelected] = useState(false);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [importLoading, setImportLoading] = useState(false);
    const csvFileInputRef = useRef(null);

    // Status options based on the API response structure
    const statusOptions = [
        { value: "", label: "All Statuses" },
        { value: "1", label: "Pending Acceptance" },
        { value: "2", label: "Pending Review" },
        { value: "3", label: "Accepted" },
        { value: "4", label: "Rejected" },
        { value: "5", label: "Canceled" },
        { value: "6", label: "Draft" }
    ];

    const paymentOptions = [
        { value: "", label: "All Payments" },
        { value: "1", label: "Paid" },
        { value: "0", label: "Unpaid" }
    ];

    const fetchTransfers = async () => {
        if (!pharmacyBranchId || !fromDate || !toDate) return;

        setLoading(true);
        try {
            const result = await TransferInRequestService.GetTransfersByBranch(
                pharmacyBranchId,
                statusFilter || null,
                paymentFilter !== "" ? parseInt(paymentFilter) : null,
                fromDate,
                toDate,
                page,
                limit
            );

            if (result?.error === null) {
                setTransfers(result?.data?.data || []);
                setTotal(result?.data?.total || 0);
                // Clear selections when data changes
                setSelectedTransfers(new Set());
                setIsAllSelected(false);
            } else {
                console.error("Error fetching transfers:", result?.error);
                setTransfers([]);
                setTotal(0);
            }
        } catch (error) {
            console.error("Error fetching transfers:", error);
            setTransfers([]);
            setTotal(0);
        } finally {
            setLoading(false);
        }
    };

    // Bulk selection helper functions
    const handleSelectAll = () => {
        if (isAllSelected) {
            setSelectedTransfers(new Set());
            setIsAllSelected(false);
        } else {
            const allIds = new Set(transfers.map(transfer => transfer.RequestId));
            setSelectedTransfers(allIds);
            setIsAllSelected(true);
        }
    };

    const handleSelectTransfer = (requestId) => {
        const newSelected = new Set(selectedTransfers);
        if (newSelected.has(requestId)) {
            newSelected.delete(requestId);
        } else {
            newSelected.add(requestId);
        }
        setSelectedTransfers(newSelected);
        setIsAllSelected(newSelected.size === transfers.length && transfers.length > 0);
    };

    // Update isAllSelected when selectedTransfers changes
    React.useEffect(() => {
        setIsAllSelected(selectedTransfers.size === transfers.length && transfers.length > 0);
    }, [selectedTransfers.size, transfers.length]);

    // Bulk toggle payment status
    const bulkTogglePaymentStatus = async () => {
        if (selectedTransfers.size === 0) {
            await Swal.fire('No Selection', 'Please select transfers to toggle payment status.', 'warning');
            return;
        }

        const selectedIds = Array.from(selectedTransfers);
        const selectedTransferData = transfers.filter(t => selectedIds.includes(t.RequestId));
        const paidCount = selectedTransferData.filter(t => t.isPaid).length;
        const unpaidCount = selectedTransferData.length - paidCount;

        try {
            const result = await Swal.fire({
                title: 'Bulk Toggle Payment Status',
                html: `
                    <p>You have selected <strong>${selectedTransfers.size}</strong> transfers:</p>
                    <ul style="text-align: left; display: inline-block;">
                        <li><strong>${paidCount}</strong> currently paid</li>
                        <li><strong>${unpaidCount}</strong> currently unpaid</li>
                    </ul>
                    <p>This will toggle the payment status for all selected transfers.</p>
                `,
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, toggle all!',
                cancelButtonText: 'Cancel'
            });

            if (result.isConfirmed) {
                setBulkLoading(true);
                try {
                    const response = await TransferInRequestService.BulkTogglePaymentStatus(selectedIds);

                    if (response?.error === null || response?.status === true || response?.statusCode === 200) {
                        await Swal.fire(
                            'Updated!',
                            `Payment status has been toggled for ${selectedTransfers.size} transfers.`,
                            'success'
                        );
                        fetchTransfers(); // Refresh the data
                        setSelectedTransfers(new Set()); // Clear selections
                    } else {
                        // Fallback simulation for testing
                        const fallbackResult = await Swal.fire({
                            title: 'API Endpoint Not Available',
                            text: `The bulk payment toggle API is not yet implemented. Would you like to simulate the changes locally for testing?`,
                            icon: 'warning',
                            showCancelButton: true,
                            confirmButtonColor: '#3085d6',
                            cancelButtonColor: '#d33',
                            confirmButtonText: 'Yes, simulate locally'
                        });

                        if (fallbackResult.isConfirmed) {
                            // Simulate local bulk update
                            setTransfers(prevTransfers =>
                                prevTransfers.map(transfer =>
                                    selectedIds.includes(transfer.RequestId)
                                        ? { ...transfer, isPaid: !transfer.isPaid }
                                        : transfer
                                )
                            );

                            await Swal.fire(
                                'Simulated!',
                                `Payment status has been simulated for ${selectedTransfers.size} transfers. (This is a local simulation only)`,
                                'info'
                            );
                            setSelectedTransfers(new Set()); // Clear selections
                        }
                    }
                } catch (apiError) {
                    console.error("API Error bulk toggling payment status:", apiError);

                    // Offer simulation as fallback for any API error
                    const fallbackResult = await Swal.fire({
                        title: 'API Error',
                        text: `Unable to update payment status via API. Would you like to simulate the changes locally for testing?`,
                        icon: 'warning',
                        showCancelButton: true,
                        confirmButtonColor: '#3085d6',
                        cancelButtonColor: '#d33',
                        confirmButtonText: 'Yes, simulate locally',
                        cancelButtonText: 'Cancel'
                    });

                    if (fallbackResult.isConfirmed) {
                        // Simulate local bulk update
                        setTransfers(prevTransfers =>
                            prevTransfers.map(transfer =>
                                selectedIds.includes(transfer.RequestId)
                                    ? { ...transfer, isPaid: !transfer.isPaid }
                                    : transfer
                            )
                        );

                        await Swal.fire(
                            'Simulated!',
                            `Payment status has been simulated for ${selectedTransfers.size} transfers. (This is a local simulation only)`,
                            'info'
                        );
                        setSelectedTransfers(new Set()); // Clear selections
                    }
                } finally {
                    setBulkLoading(false);
                }
            }
        } catch (error) {
            console.error("Unexpected error in bulkTogglePaymentStatus:", error);
            await Swal.fire(
                'Error!',
                'An unexpected error occurred. Please try again.',
                'error'
            );
            setBulkLoading(false);
        }
    };

    const togglePaymentStatus = async (requestId, currentStatus) => {
        try {
            const result = await Swal.fire({
                title: 'Toggle Payment Status',
                text: `Mark this transfer as ${currentStatus ? 'unpaid' : 'paid'}?`,
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: `Yes, mark as ${currentStatus ? 'unpaid' : 'paid'}!`
            });

            if (result.isConfirmed) {
                try {
                    const response = await TransferInRequestService.TogglePaymentStatus(requestId);

                    if (response?.error === null || response?.status === true || response?.statusCode === 200) {
                        await Swal.fire(
                            'Updated!',
                            `Payment status has been updated to ${currentStatus ? 'unpaid' : 'paid'}.`,
                            'success'
                        );
                        fetchTransfers();
                    } else {
                        const fallbackResult = await Swal.fire({
                            title: 'API Endpoint Not Available',
                            text: `The payment toggle API is not yet implemented. Would you like to simulate the change locally for testing?`,
                            icon: 'warning',
                            showCancelButton: true,
                            confirmButtonColor: '#3085d6',
                            cancelButtonColor: '#d33',
                            confirmButtonText: 'Yes, simulate locally'
                        });

                        if (fallbackResult.isConfirmed) {
                            setTransfers(prevTransfers =>
                                prevTransfers.map(transfer =>
                                    transfer.RequestId === requestId
                                        ? { ...transfer, isPaid: !currentStatus }
                                        : transfer
                                )
                            );

                            await Swal.fire(
                                'Simulated!',
                                `Payment status has been simulated as ${currentStatus ? 'unpaid' : 'paid'}. (This is a local simulation only)`,
                                'info'
                            );
                        }
                    }
                } catch (apiError) {
                    console.error("API Error toggling payment status:", apiError);

                    const fallbackResult = await Swal.fire({
                        title: 'API Error',
                        text: `Unable to update payment status via API. Would you like to simulate the change locally for testing?`,
                        icon: 'warning',
                        showCancelButton: true,
                        confirmButtonColor: '#3085d6',
                        cancelButtonColor: '#d33',
                        confirmButtonText: 'Yes, simulate locally',
                        cancelButtonText: 'Cancel'
                    });

                    if (fallbackResult.isConfirmed) {
                        setTransfers(prevTransfers =>
                            prevTransfers.map(transfer =>
                                transfer.RequestId === requestId
                                    ? { ...transfer, isPaid: !currentStatus }
                                    : transfer
                            )
                        );

                        await Swal.fire(
                            'Simulated!',
                            `Payment status has been simulated as ${currentStatus ? 'unpaid' : 'paid'}. (This is a local simulation only)`,
                            'info'
                        );
                    }
                }
            }
        } catch (error) {
            console.error("Unexpected error in togglePaymentStatus:", error);
            await Swal.fire(
                'Error!',
                'An unexpected error occurred. Please try again.',
                'error'
            );
        }
    };

    const normalizeCsvHeader = (header) => String(header || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');

    const parseCsvLine = (line) => {
        const result = [];
        let current = '';
        let inQuotes = false;

        for (let i = 0; i < line.length; i++) {
            const char = line[i];
            if (char === '"') {
                if (inQuotes && line[i + 1] === '"') {
                    current += '"';
                    i++;
                } else {
                    inQuotes = !inQuotes;
                }
            } else if (char === ',' && !inQuotes) {
                result.push(current.trim());
                current = '';
            } else {
                current += char;
            }
        }

        result.push(current.trim());
        return result;
    };

    const parseMarkPaidCsv = (csvText) => {
        const lines = csvText.replace(/^\uFEFF/, '').split(/\r?\n/).filter((line) => line.trim() !== '');
        if (lines.length < 2) {
            throw new Error('CSV file must include a header row and at least one data row.');
        }

        const headers = parseCsvLine(lines[0]).map(normalizeCsvHeader);
        const requestIdIndex = headers.findIndex((header) => header === 'requestid' || header === 'id');
        const rxNumberIndex = headers.findIndex((header) => header === 'rxnumber' || header === 'rx');

        if (requestIdIndex === -1 || rxNumberIndex === -1) {
            throw new Error('CSV must include "Request ID" and "Rx Number" columns.');
        }

        const items = [];
        for (let i = 1; i < lines.length; i++) {
            const columns = parseCsvLine(lines[i]);
            const requestId = Number(String(columns[requestIdIndex] || '').replace(/^#/, '').trim());
            const rxNumber = String(columns[rxNumberIndex] || '').trim();

            if (requestId > 0 && rxNumber) {
                items.push({ requestId, rxNumber });
            }
        }

        if (items.length === 0) {
            throw new Error('No valid rows found. Each row needs a Request ID and Rx Number.');
        }

        return items;
    };

    const handleCsvFileSelected = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';

        if (!file) return;

        try {
            const csvText = await file.text();
            const items = parseMarkPaidCsv(csvText);

            const previewRows = items.slice(0, 5).map((item) => `#${item.requestId} - ${item.rxNumber}`).join('<br>');
            const moreRowsText = items.length > 5 ? `<br><em>...and ${items.length - 5} more</em>` : '';

            const confirmResult = await Swal.fire({
                title: 'Import CSV & Mark Paid',
                html: `
                    <p>Found <strong>${items.length}</strong> valid rows in <strong>${file.name}</strong>.</p>
                    <p style="text-align:left; margin: 0 auto; display:inline-block;">
                        ${previewRows}${moreRowsText}
                    </p>
                    <p class="mt-3">Matching transfers will be marked as <strong>Paid</strong>.</p>
                `,
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, mark as paid',
                cancelButtonText: 'Cancel'
            });

            if (!confirmResult.isConfirmed) return;

            setImportLoading(true);
            const response = await TransferInRequestService.BulkMarkPaymentStatus(items);

            if (response?.error === null || response?.status === true || response?.statusCode === 200) {
                const summary = response?.data?.summary || {};
                const notFound = response?.data?.notFound || [];
                const notFoundPreview = notFound.slice(0, 5).map((row) => `#${row.requestId} - ${row.rxNumber}`).join('<br>');
                const notFoundMore = notFound.length > 5 ? `<br><em>...and ${notFound.length - 5} more</em>` : '';

                await Swal.fire({
                    title: 'Import Complete',
                    html: `
                        <ul style="text-align:left; display:inline-block;">
                            <li><strong>${summary.markedPaid || 0}</strong> marked as paid</li>
                            <li><strong>${summary.alreadyPaid || 0}</strong> already paid</li>
                            <li><strong>${summary.notFound || 0}</strong> not found / mismatch</li>
                        </ul>
                        ${notFound.length > 0 ? `<p style="text-align:left;">Not found:</p><p style="text-align:left;">${notFoundPreview}${notFoundMore}</p>` : ''}
                    `,
                    icon: (summary.notFound || 0) > 0 ? 'warning' : 'success'
                });
                fetchTransfers();
            } else {
                await Swal.fire(
                    'Error',
                    response?.message || response?.error || 'Unable to mark transfers as paid.',
                    'error'
                );
            }
        } catch (error) {
            console.error('Error importing CSV:', error);
            await Swal.fire(
                'Invalid CSV',
                error?.message || 'Unable to read the CSV file.',
                'error'
            );
        } finally {
            setImportLoading(false);
        }
    };

    const exportToPDF = async () => {
        const { jsPDF } = await import("jspdf");
        const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const margin = 10;
        const usableWidth = pageWidth - margin * 2;
        const columns = [
            { header: "Request ID", width: 28, value: (transfer) => `#${transfer.RequestId || ""}` },
            { header: "Rx Number", width: 32, value: (transfer) => transfer.RxNumber || "N/A" },
            { header: "Request Date", width: 32, value: (transfer) => transfer.RequestDate ? moment(transfer.RequestDate).format("MMM DD, YYYY") : "" },
            { header: "Patient Name", width: 48, value: (transfer) => transfer.PatientName || "N/A" },
            { header: "From Pharmacy", width: 48, value: (transfer) => transfer.FromPharmacyBranchName || "N/A" },
            { header: "To Pharmacy", width: 48, value: (transfer) => transfer.ToPharmacyBranchName || "N/A" },
            { header: "Status", width: 32, value: (transfer) => transfer.StatusLabel || "Unknown" },
            { header: "Payment", width: 22, value: (transfer) => transfer.isPaid ? "Paid" : "Unpaid" },
        ];
        const widthScale = usableWidth / columns.reduce((sum, column) => sum + column.width, 0);
        columns.forEach((column) => {
            column.width *= widthScale;
        });

        const rowHeight = 7;
        const headerBandHeight = 8;

        const drawPageHeader = () => {
            doc.setTextColor(33, 37, 41);
            doc.setFont("helvetica", "bold");
            doc.setFontSize(14);
            doc.text(`${pharmacyBranchName} Transfer Reports`, pageWidth / 2, margin + 4, { align: "center" });
            doc.setFont("helvetica", "normal");
            doc.setFontSize(9);
            doc.text(
                `Date Range: ${moment(fromDate).format("MMMM DD, YYYY")} - ${moment(toDate).format("MMMM DD, YYYY")}`,
                pageWidth / 2,
                margin + 10,
                { align: "center" }
            );

            const headerTop = margin + 16;
            doc.setFillColor(248, 249, 250);
            doc.rect(margin, headerTop, usableWidth, headerBandHeight, "F");
            doc.setFont("helvetica", "bold");
            doc.setFontSize(8);
            let x = margin;
            columns.forEach((column) => {
                doc.text(column.header, x + 1.5, headerTop + 5.2);
                x += column.width;
            });
            doc.setDrawColor(222, 226, 230);
            doc.line(margin, headerTop + headerBandHeight, margin + usableWidth, headerTop + headerBandHeight);
            return headerTop + headerBandHeight;
        };

        let y = drawPageHeader();
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);

        transfers.forEach((transfer, index) => {
            if (y + rowHeight > pageHeight - margin) {
                doc.addPage();
                y = drawPageHeader();
                doc.setFont("helvetica", "normal");
                doc.setFontSize(8);
            }

            if (index % 2 === 0) {
                doc.setFillColor(248, 249, 250);
                doc.rect(margin, y, usableWidth, rowHeight, "F");
            }

            let x = margin;
            columns.forEach((column) => {
                const text = String(column.value(transfer) ?? "");
                const fitted = doc.splitTextToSize(text, column.width - 3)[0] || "";
                doc.text(fitted, x + 1.5, y + 4.8);
                x += column.width;
            });
            y += rowHeight;
        });

        const pageCount = doc.internal.getNumberOfPages();
        for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
            doc.setPage(pageNumber);
            doc.setFont("helvetica", "normal");
            doc.setFontSize(8);
            doc.setTextColor(108, 117, 125);
            doc.text(`Page ${pageNumber} of ${pageCount}`, pageWidth - margin, pageHeight - 4, { align: "right" });
        }

        doc.save(`${pharmacyBranchName}-transfer-reports-${moment().format("YYYY-MM-DD")}.pdf`);
    };

    const exportToCSV = () => {
        const headers = [
            "Request ID",
            "Rx Number",
            "Request Date",
            "Patient Name",
            "From Pharmacy",
            "To Pharmacy",
            "Status",
            "Payment Status"
        ];

        const csvData = transfers.map(transfer => [
            transfer.RequestId || '',
            transfer.RxNumber || '',
            moment(transfer.RequestDate).format("YYYY-MM-DD") || '',
            transfer.PatientName || '',
            transfer.FromPharmacyBranchName || '',
            transfer.ToPharmacyBranchName || '',
            transfer.StatusLabel || '',
            transfer.isPaid ? 'Paid' : 'Unpaid'
        ]);

        const csvContent = [headers, ...csvData]
            .map(row => row.map(field => {
                const cleanField = String(field || '').replace(/"/g, '""');
                return `"${cleanField}"`;
            }).join(','))
            .join('\n');

        const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `${pharmacyBranchName}-transfer-reports-${moment().format('YYYY-MM-DD')}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    useEffect(() => {
        fetchTransfers();
    }, [pharmacyBranchId, fromDate, toDate, statusFilter, paymentFilter, page, limit]);

    if (loading) {
        return <Loading />;
    }

    return (
        <div className="transfer-reports-table-container">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h4>{pharmacyBranchName} Transfer Reports</h4>
                <div className="d-flex gap-2">
                    <input
                        ref={csvFileInputRef}
                        type="file"
                        accept=".csv,text/csv"
                        style={{ display: 'none' }}
                        onChange={handleCsvFileSelected}
                    />
                    <button
                        className="btn btn-warning btn-sm"
                        onClick={() => csvFileInputRef.current?.click()}
                        disabled={importLoading}
                    >
                        {importLoading ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Importing...
                            </>
                        ) : (
                            <>
                                <i className="fa-solid fa-file-import me-2"></i>
                                Import CSV & Mark Paid
                            </>
                        )}
                    </button>
                    {selectedTransfers.size > 0 && (
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={bulkTogglePaymentStatus}
                            disabled={bulkLoading}
                        >
                            {bulkLoading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Processing...
                                </>
                            ) : (
                                <>
                                    <i className="fa-solid fa-toggle-on me-2"></i>
                                    Bulk Toggle ({selectedTransfers.size})
                                </>
                            )}
                        </button>
                    )}
                    <button
                        className="btn btn-success btn-sm"
                        onClick={exportToCSV}
                        disabled={transfers.length === 0}
                    >
                        <i className="fa-solid fa-file-csv me-2"></i>
                        Export CSV
                    </button>
                    <button
                        className="btn btn-danger btn-sm"
                        onClick={exportToPDF}
                        disabled={transfers.length === 0}
                    >
                        <i className="fa-solid fa-file-pdf me-2"></i>
                        Export PDF
                    </button>
                </div>
            </div>

            {fromDate && toDate && (
                <div className="alert alert-info">
                    <i className="fa-solid fa-calendar me-2"></i>
                    Showing transfers from <strong>{moment(fromDate).format('MMMM DD, YYYY')}</strong> to <strong>{moment(toDate).format('MMMM DD, YYYY')}</strong>
                </div>
            )}

            {/* Filters */}
            <div className="row mb-3">
                <div className="col-md-4">
                    <label className="form-label">Status Filter:</label>
                    <select
                        className="form-select"
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setPage(1); // Reset to first page when filtering
                        }}
                    >
                        {statusOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="col-md-4">
                    <label className="form-label">Payment Filter:</label>
                    <select
                        className="form-select"
                        value={paymentFilter}
                        onChange={(e) => {
                            setPaymentFilter(e.target.value);
                            setPage(1); // Reset to first page when filtering
                        }}
                    >
                        {paymentOptions.map(option => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="col-md-4 d-flex align-items-end">
                    <button
                        className="btn btn-outline-secondary"
                        onClick={() => {
                            setStatusFilter("");
                            setPaymentFilter("");
                            setPage(1);
                        }}
                    >
                        <i className="fa-solid fa-filter-circle-xmark me-2"></i>
                        Clear Filters
                    </button>
                </div>
            </div>

            {/* Results Summary */}
            <div className="mb-3 d-flex justify-content-between align-items-center">
                <small className="text-muted">
                    Showing {transfers.length} of {total} transfers
                    {statusFilter && ` • Status: ${statusOptions.find(s => s.value === statusFilter)?.label}`}
                    {paymentFilter !== "" && ` • Payment: ${paymentOptions.find(p => p.value === paymentFilter)?.label}`}
                </small>
                {selectedTransfers.size > 0 && (
                    <small className="text-primary fw-bold">
                        <i className="fa-solid fa-check-square me-1"></i>
                        {selectedTransfers.size} selected
                        <button
                            className="btn btn-link btn-sm p-0 ms-2 text-decoration-none"
                            onClick={() => setSelectedTransfers(new Set())}
                            title="Clear selection"
                        >
                            <i className="fa-solid fa-times"></i>
                        </button>
                    </small>
                )}
            </div>

            {/* Table */}
            <div className="card">
                <div className="card-body p-0">
                    <div className="table-responsive" id="transfer-reports-table">
                        <table className="table table-hover mb-0">
                            <thead className="table-light">
                                <tr>
                                    <th style={{ width: '50px' }}>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                checked={isAllSelected}
                                                onChange={handleSelectAll}
                                                disabled={transfers.length === 0}
                                                title="Select all transfers"
                                            />
                                        </div>
                                    </th>
                                    <th>Request ID</th>
                                    <th>Rx Number</th>
                                    <th>Request Date</th>
                                    <th>Patient Name</th>
                                    <th>From Pharmacy</th>
                                    <th>To Pharmacy</th>
                                    <th>Status</th>
                                    <th>Payment</th>
                                    <th className="action-header">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {transfers.length === 0 ? (
                                    <tr>
                                        <td colSpan="10" className="text-center py-4">
                                            <i className="fa-solid fa-inbox fa-2x text-muted mb-2"></i>
                                            <p className="text-muted mb-0">No transfers found for the selected criteria</p>
                                        </td>
                                    </tr>
                                ) : (
                                    transfers.map((transfer, index) => (
                                        <tr key={transfer.RequestId || index} className={selectedTransfers.has(transfer.RequestId) ? 'table-active' : ''}>
                                            <td>
                                                <div className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="checkbox"
                                                        checked={selectedTransfers.has(transfer.RequestId)}
                                                        onChange={() => handleSelectTransfer(transfer.RequestId)}
                                                        title="Select this transfer"
                                                    />
                                                </div>
                                            </td>
                                            <td>
                                                <strong>#{transfer.RequestId}</strong>
                                                {transfer?.Notes && transfer.Notes.includes("[important!!]") && (
                                                    <span 
                                                        className="ms-2" 
                                                        title="Important RX to view"
                                                        style={{ cursor: "help" }}
                                                    >
                                                        <i className="fa fa-exclamation-triangle text-warning"></i>
                                                    </span>
                                                )}
                                            </td>
                                            <td>
                                                {transfer.RxNumber || 'N/A'}
                                            </td>
                                            <td>{moment(transfer.RequestDate).format('MMM DD, YYYY')}</td>
                                            <td>{transfer.PatientName || 'N/A'}</td>
                                            <td>
                                                <small className="text-muted d-block">{transfer.FromPharmacyBranchName || 'N/A'}</small>
                                            </td>
                                            <td>
                                                <small className="text-muted d-block">{transfer.ToPharmacyBranchName || 'N/A'}</small>
                                            </td>
                                            <td>
                                                <span className={`badge ${transfer.StatusId === 1 || transfer.StatusId === 2 ? 'bg-warning' :
                                                    transfer.StatusId === 3 ? 'bg-success' :
                                                        transfer.StatusId === 4 || transfer.StatusId === 5 ? 'bg-danger' :
                                                            'bg-secondary'
                                                    }`}>
                                                    {transfer.StatusLabel || 'Unknown'}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`badge ${transfer.isPaid ? 'bg-success' : 'bg-warning'}`}>
                                                    {transfer.isPaid ? 'Paid' : 'Unpaid'}
                                                </span>
                                            </td>
                                            <td className="action-cell">
                                                <button
                                                    className={`btn btn-sm ${transfer.isPaid ? 'btn-outline-warning' : 'btn-outline-success'}`}
                                                    onClick={() => togglePaymentStatus(transfer.RequestId, transfer.isPaid)}
                                                    title={`Mark as ${transfer.isPaid ? 'unpaid' : 'paid'}`}
                                                >
                                                    <i className={`fa-solid ${transfer.isPaid ? 'fa-times' : 'fa-check'}`}></i>
                                                    {transfer.isPaid ? 'Mark Unpaid' : 'Mark Paid'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Pagination */}
            {total > limit && (
                <div className="mt-3">
                    <Pagination
                        page={page}
                        total={total}
                        limit={limit}
                        setPage={setPage}
                    />
                </div>
            )}
        </div>
    );
};

export default TransferReportsTable;
